import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../styles/NammaPass.css";
import { SvgCornerOrn, SvgMeshGrid } from "./SvgDecorations";

const POSTS: Record<string, {
  category: string;
  title: string;
  readTime: string;
  impact: string;
  accent: string;
  body: string;
}> = {
  "sustainable-actions-climate-impact": {
    category: "SUSTAINABILITY • CLIMATE TECH",
    title: "Turning Everyday Sustainable Actions into Climate Impact",
    readTime: "8 min read",
    impact: "Global Impact",
    accent: "#8cff2e",
    body: `Discover how individual choices can create measurable environmental change through innovative carbon tracking and reward systems that empower citizens to become climate champions.

**The Power of Individual Action**

Every sustainable choice you make — taking public transport, reducing waste, choosing plant-based meals — has a measurable carbon impact. The challenge has always been making that impact visible and rewarding.

**Carbon Tracking at Scale**

Modern technology now allows us to track individual carbon footprints with unprecedented accuracy. By combining GPS data, purchase history, and behavioral patterns, platforms like Reker can calculate the exact CO2 savings from each sustainable action.

**Reward Systems That Work**

The key to driving behavior change is immediate, tangible rewards. When commuters see real-time cashback and carbon credits accumulating with every metro ride, the motivation to choose sustainable transport becomes self-reinforcing.

**Building a Climate-Conscious Community**

Individual actions, when aggregated across millions of users, create city-scale climate impact. Bengaluru's metro network alone, if fully adopted digitally, could offset thousands of tonnes of CO2 annually.

**The Road Ahead**

As climate technology matures, the gap between individual action and global impact will continue to shrink. Platforms that make sustainability rewarding — not just virtuous — will drive the next wave of climate action.

Visit [reker.world](https://reker.world) to learn more about how your daily commute can become a climate action.`,
  },
  "carbon-offsets-guide": {
    category: "ENVIRONMENT • CLIMATE SOLUTIONS",
    title: "Carbon Offsets: A Complete Guide to Climate Action",
    readTime: "10 min read",
    impact: "Climate Education",
    accent: "#a78bfa",
    body: `Understanding the science, economics, and impact of carbon offsets — from voluntary markets to compliance mechanisms driving global decarbonization efforts.

**What Are Carbon Offsets?**

A carbon offset represents the reduction of one metric tonne of CO2 (or equivalent greenhouse gas) from the atmosphere. When you purchase or earn an offset, you're funding an activity that removes or prevents that amount of carbon from entering the atmosphere.

**Voluntary vs Compliance Markets**

The voluntary carbon market allows individuals and companies to offset emissions beyond regulatory requirements. The compliance market, by contrast, is mandated by governments — companies must offset emissions above their allocated cap.

**Types of Carbon Offset Projects**

Reforestation and afforestation projects plant trees that absorb CO2. Renewable energy projects replace fossil fuel generation. Methane capture projects prevent potent greenhouse gases from reaching the atmosphere. Energy efficiency projects reduce overall consumption.

**The Quality Question**

Not all offsets are equal. High-quality offsets must be additional (wouldn't have happened without the funding), permanent, verifiable, and independently certified by bodies like Gold Standard or Verra.

**Personal Carbon Credits — The Next Frontier**

Reker is pioneering Personal Carbon Credits (PCCs) — a system where individuals earn verified carbon credits for sustainable lifestyle choices. This democratizes access to carbon markets and makes every citizen a participant in the climate solution.

Learn more at [reker.world](https://reker.world).`,
  },
  "namma-pass-digital-metro-pass": {
    category: "METRO • DIGITAL MOBILITY",
    title: "Namma Metro Mobile QR Code Passes, Digital Smart Metro Pass and Unlimited Travel with Namma Pass",
    readTime: "7 min read",
    impact: "Urban Mobility",
    accent: "#8cff2e",
    body: `Daily travel on [Namma Metro](https://english.bmrc.co.in) in Bengaluru is becoming easier with the introduction of Namma Pass, a smartphone-based digital metro pass and metro ticket booking solution designed for modern urban commuters.

Built to support seamless travel across the metro network operated by [BMRCL](https://english.bmrc.co.in) (Bangalore Metro Rail Corporation Limited), Namma Pass allows users to manage metro passes and metro tickets directly from their phones without needing physical smart cards or standing in queues.

**Book Metro Tickets and Use Digital Metro Pass Easily**

With Namma Pass, commuters can book metro tickets digitally and access metro services using a virtual metro pass. This mobile-first solution helps metro users:

- Avoid long queues at metro ticket counters
- Manage metro travel in one app
- Save time during peak-hour commuting
- Enjoy a smooth tap-and-ride metro pass experience

The platform is designed for students, working professionals, and daily commuters who rely on [Namma Metro](https://english.bmrc.co.in) for convenient city travel.

**₹199 Unlimited Namma Metro Pass - Travel Smarter**

One of the key benefits of Namma Pass is the ₹199 unlimited metro pass plan, which helps commuters plan their travel expenses better. The digital metro pass supports:

- Predictable metro travel costs
- Easy metro QR ticket management
- Flexible commuting options all in one QR for daily rides
- A cost-effective solution for daily metro riders with bigger savings

**Get Up to 20% Cashback on Namma Metro Rides**

Users can explore up to 20% ride value benefits or cashback on metro tickets booked through Namma Pass. This travel savings feature positions the platform as one of the biggest commute-saving digital metro pass solutions in Bengaluru. By combining convenience with affordability, Namma Pass aims to make [Namma Metro](https://english.bmrc.co.in) travel more rewarding for everyday commuters.

**Earn Carbon Credits Rewards While Using Namma Metro**

In addition to travel savings, commuters can also earn carbon credits rewards on metro rides, encouraging sustainable travel habits such as using public transport and walking for first-mile connectivity. This feature helps users understand the positive environmental impact of choosing metro commuting over private vehicle usage.

To learn more about the mobility rewards ecosystem, visit [reker.world](https://reker.world).

**No Metro Cards, No Queues - Just Tap and Ride**

Namma Pass eliminates the need for physical metro smart cards by offering a digital metro pass experience. Commuters can simply use their smartphones to tap and ride on [Namma Metro](https://english.bmrc.co.in), avoiding the hassle of manual ticketing. This reflects the growing shift toward contactless metro ticketing and smart city mobility solutions supported by [BMRCL](https://english.bmrc.co.in) and [ONDC](https://ondc.org).

**Start Using Namma Pass for Your Daily Metro Commute**

As Bengaluru's metro network continues to expand, digital metro pass platforms like Namma Pass are expected to play an important role in improving commuter convenience and encouraging greater adoption of public transport.

Start commuting smarter - explore metro pass plans at [nammapass.com](https://nammapass.com). Download the Reker App! Daily Metro? Daily Reward-o!`,
  },
};

// Render body text: **bold** → <strong>, bullet lines → list items
const renderBody = (body: string) => {
  const paragraphs = body.split("\n\n");
  return paragraphs.map((para, i) => {
    if (para.startsWith("**") && para.endsWith("**")) {
      return (
        <h3 key={i} className="bp-subheading">
          {para.slice(2, -2)}
        </h3>
      );
    }
    const lines = para.split("\n");
    const isList = lines.every(l => l.startsWith("- "));
    if (isList) {
      return (
        <ul key={i} className="bp-list">
          {lines.map((l, j) => <li key={j}>{l.slice(2)}</li>)}
        </ul>
      );
    }
    // inline bold + links
    const parts = para.split(/(\*\*.*?\*\*|\[.*?\]\(.*?\))/g);
    return (
      <p key={i} className="bp-para">
        {parts.map((part, j) => {
          const boldMatch = part.match(/^\*\*(.*?)\*\*$/);
          if (boldMatch) return <strong key={j}>{boldMatch[1]}</strong>;
          const linkMatch = part.match(/^\[(.*?)\]\((.*?)\)$/);
          if (linkMatch) return <a key={j} href={linkMatch[2]} target="_blank" rel="noopener noreferrer" style={{ color: "#8cff2e", textDecoration: "underline" }}>{linkMatch[1]}</a>;
          return part;
        })}
      </p>
    );
  });
};

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const post = slug ? POSTS[slug] : null;

  useEffect(() => { window.scrollTo(0, 0); }, []);

  if (!post) {
    return (
      <div className="np-root bp-not-found">
        <p>Post not found.</p>
        <button className="np-cta-primary" onClick={() => navigate("/blog")}>← Back to Blog</button>
      </div>
    );
  }

  return (
    <div className="np-root" style={{ paddingTop: 76 }}>
      <div className="bp-hero" style={{ borderBottom: `1px solid ${post.accent}22` }}>
        <SvgMeshGrid opacity={0.04} />
        <SvgCornerOrn position="tl" />
        <SvgCornerOrn position="tr" />
        <div className="bp-hero-inner">
          <button className="bp-back" onClick={() => navigate("/blog")}>
            ← Back to Blog
          </button>
          <div className="blog-category" style={{
            color: post.accent,
            borderColor: `${post.accent}33`,
            background: `${post.accent}10`,
            marginBottom: 24,
          }}>
            {post.category}
          </div>
          <h1 className="bp-title">{post.title}</h1>
          <div className="bp-meta">
            <span>⏱ {post.readTime}</span>
            <span>🌍 {post.impact}</span>
          </div>
          <div className="bp-accent-line" style={{ background: `linear-gradient(90deg, ${post.accent}, transparent)` }} />
        </div>
      </div>

      <article className="bp-body">
        {renderBody(post.body)}
      </article>

      <div className="bp-footer-nav">
        <button className="np-cta-primary" onClick={() => navigate("/blog")}>
          ← Back to Blog
        </button>
      </div>
    </div>
  );
};

export default BlogPost;
